/**
 * @module Helper
 */

import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '@app/services/auth.service';

/**
 * Interceptor for handling HTTP errors in the Angular application.
 *
 * This service implements the HttpInterceptor interface to intercept outgoing HTTP requests
 * and handle errors returned from the server. It specifically checks for 401 Unauthorized
 * responses and triggers a logout action if such a response is received.
 *
 * @helper
 * @@Injectable
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
	/**
	 * Creates an instance of the ErrorInterceptor.
	 *
	 * @constructor
	 * @param {AuthService} _authService - The authentication service used to manage user sessions.
	 */
	constructor(private _authService: AuthService) {}

	/**
	 * Intercepts an outgoing HTTP request and handles any errors that occur during the request.
	 *
	 * @param {HttpRequest<any>} request - The HTTP request to intercept.
	 * @param {HttpHandler} next - The next handler in the chain to process the request.
	 * @returns {Observable<HttpEvent<any>>} An observable of the HTTP event stream, which may emit an error.
	 */
	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			catchError((err) => {
				if (err.status === 401) {
					// Auto logout if 401 response returned from API.
					this._authService.logout();
					location.reload();
				}

				// @see {@link https://stackoverflow.com/a/30469297}
				const error = err.error.message || err.statusText;
				return throwError(() => new Error(error));
			})
		);
	}
}
